import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User,
  Key,
  CreditCard,
  Receipt,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  Zap,
  TrendingUp,
  Calendar,
  Shield,
  Mail,
  Loader2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import LicenseManagement from "@/components/profile/LicenseManagement";

const Settings = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabParam || "profile");

  useEffect(() => {
    if (tabParam && ["profile", "password", "licenses", "credits", "payments"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    setSearchParams({ tab: val });
  };

  // Password state
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // OAuth user password setup state
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [sendingSetupEmail, setSendingSetupEmail] = useState(false);
  const [setupEmailSent, setSetupEmailSent] = useState(false);

  const [paymentLog, setPaymentLog] = useState<any[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Use real profile data
  const realUser = {
    name: profile?.display_name || user?.email?.split("@")[0] || "User",
    email: user?.email || "",
    credits: profile?.credits || 0,
    totalSpent: paymentLog.reduce((sum, p) => p.status === 'completed' ? sum + p.amount : sum, 0),
    memberSince: profile?.created_at
      ? new Date(profile.created_at).toLocaleDateString("th-TH", { year: 'numeric', month: 'long' })
      : "-",
  };

  useEffect(() => {
    if (user) {
      fetchPaymentLog();
      // Check if user logged in via OAuth (no password)
      checkIfOAuthUser();
    }
  }, [user]);

  // Check if user is OAuth-only (no password set)
  const checkIfOAuthUser = async () => {
    if (!user) return;
    // Users who signed in via OAuth have identities but no password
    // We can check by looking at app_metadata.provider or identities
    const identities = user.identities || [];
    const hasEmailProvider = identities.some((id: any) => id.provider === 'email');
    setIsOAuthUser(!hasEmailProvider && identities.length > 0);
  };

  // Send password setup email for OAuth users
  const handleSendPasswordSetupEmail = async () => {
    if (!user?.email) return;

    setSendingSetupEmail(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/settings?tab=password&setup=true`
      });

      if (error) throw error;

      setSetupEmailSent(true);
      toast({
        title: t("ส่งอีเมลสำเร็จ", "Email Sent"),
        description: t(
          "กรุณาเช็กอีเมลของคุณเพื่อตั้งรหัสผ่าน",
          "Please check your email to set up your password."
        ),
      });
    } catch (err: any) {
      console.error("Error sending password setup email:", err);
      toast({
        title: t("ข้อผิดพลาด", "Error"),
        description: err.message || t("ไม่สามารถส่งอีเมลได้", "Failed to send email"),
        variant: "destructive",
      });
    } finally {
      setSendingSetupEmail(false);
    }
  };

  const fetchPaymentLog = async () => {
    setLoadingPayments(true);
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          id,
          amount,
          created_at,
          payment_method,
          status,
          products (name)
        `)
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPaymentLog(data || []);
    } catch (err) {
      console.error("Error fetching payment log:", err);
    } finally {
      setLoadingPayments(false);
    }
  };

  const [creditHistory, setCreditHistory] = useState<any[]>([]);
  const [loadingCredits, setLoadingCredits] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCreditHistory();
    }
  }, [user]);

  const fetchCreditHistory = async () => {
    setLoadingCredits(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("*")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const mappedHistory = (data || []).map(log => ({
        id: log.id,
        type: "usage",
        amount: -log.credits_used,
        date: new Date(log.created_at).toLocaleString("th-TH"),
        description: `${log.service_type.toUpperCase()} - ${log.model}`,
        status: log.status
      }));

      setCreditHistory(mappedHistory);
    } catch (err) {
      console.error("Error fetching credit history:", err);
    } finally {
      setLoadingCredits(false);
    }
  };


  const handleChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: t("ข้อผิดพลาด", "Error"),
        description: t("กรุณากรอกรหัสผ่านให้ครบทุกช่อง", "Please fill in all password fields."),
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: t("ข้อผิดพลาด", "Error"),
        description: t("รหัสผ่านใหม่ไม่ตรงกัน", "New passwords do not match."),
        variant: "destructive",
      });
      return;
    }
    if (newPassword.length < 6) {
      toast({
        title: t("ข้อผิดพลาด", "Error"),
        description: t("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร", "Password must be at least 6 characters."),
        variant: "destructive",
      });
      return;
    }
    toast({
      title: t("อัปเดตรหัสผ่านสำเร็จ", "Password Updated"),
      description: t("รหัสผ่านของคุณถูกเปลี่ยนเรียบร้อยแล้ว", "Your password has been changed successfully."),
    });
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-bold">{t("ตั้งค่าบัญชี", "Account Settings")}</h1>
            <p className="text-muted-foreground">{t("จัดการบัญชีและดูประวัติของคุณ", "Manage your account and view history")}</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card variant="glass">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("เครดิตปัจจุบัน", "Current Credits")}</p>
                  <p className="text-xl font-bold text-primary">{realUser.credits.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("ยอดใช้จ่ายทั้งหมด", "Total Spent")}</p>
                  <p className="text-xl font-bold">฿{realUser.totalSpent.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card variant="glass">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t("สมาชิกตั้งแต่", "Member Since")}</p>
                  <p className="text-xl font-bold">{realUser.memberSince}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" />
              {t("โปรไฟล์", "Profile")}
            </TabsTrigger>
            <TabsTrigger value="password" className="gap-2">
              <Key className="h-4 w-4" />
              {t("เปลี่ยนรหัสผ่าน", "Change Password")}
            </TabsTrigger>
            <TabsTrigger value="licenses" className="gap-2">
              <Shield className="h-4 w-4" />
              {t("โปรแกรมของฉัน", "My Programs")}
            </TabsTrigger>
            <TabsTrigger value="credits" className="gap-2">
              <Zap className="h-4 w-4" />
              {t("ประวัติเครดิต", "Credit History")}
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2">
              <Receipt className="h-4 w-4" />
              {t("ประวัติการชำระเงิน", "Payment Log")}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card variant="glass">
              <CardHeader>
                <CardTitle>{t("ข้อมูลโปรไฟล์", "Profile Information")}</CardTitle>
                <CardDescription>{t("อัปเดตรายละเอียดบัญชีของคุณ", "Update your account details")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("ชื่อที่แสดง", "Display Name")}</Label>
                  <Input id="name" defaultValue={realUser.name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("ที่อยู่อีเมล", "Email Address")}</Label>
                  <Input id="email" type="email" defaultValue={realUser.email} disabled />
                </div>
                <div className="pt-4">
                  <Button className="gap-2">
                    <Save className="h-4 w-4" />
                    {t("บันทึกการเปลี่ยนแปลง", "Save Changes")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password">
            <Card variant="glass">
              <CardHeader>
                <CardTitle>{t("เปลี่ยนรหัสผ่าน", "Change Password")}</CardTitle>
                <CardDescription>{t("อัปเดตรหัสผ่านบัญชีของคุณ", "Update your account password")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 max-w-md">
                {/* OAuth User - No password set yet */}
                {isOAuthUser ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Mail className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-primary">
                            {t("คุณลงชื่อเข้าใช้ด้วย Google", "You signed in with Google")}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {t(
                              "บัญชีของคุณยังไม่มีรหัสผ่าน คุณสามารถตั้งรหัสผ่านเพื่อใช้ login ด้วย Email ได้",
                              "Your account doesn't have a password yet. You can set one to also login with email."
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {setupEmailSent ? (
                      <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                        <div className="flex items-center gap-2 text-green-500">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">
                            {t("ส่งอีเมลแล้ว!", "Email sent!")}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          {t(
                            `กรุณาเช็กอีเมล ${user?.email} และคลิกลิงก์เพื่อตั้งรหัสผ่าน`,
                            `Please check ${user?.email} and click the link to set your password.`
                          )}
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={handleSendPasswordSetupEmail}
                        disabled={sendingSetupEmail}
                        className="gap-2"
                      >
                        {sendingSetupEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        {t("ส่งอีเมลเพื่อตั้งรหัสผ่าน", "Send Password Setup Email")}
                      </Button>
                    )}
                  </div>
                ) : (
                  /* Regular user - Has password */
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="current-password">{t("รหัสผ่านปัจจุบัน", "Current Password")}</Label>
                      <div className="relative">
                        <Input
                          id="current-password"
                          type={showCurrentPass ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowCurrentPass(!showCurrentPass)}
                        >
                          {showCurrentPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">{t("รหัสผ่านใหม่", "New Password")}</Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showNewPass ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowNewPass(!showNewPass)}
                        >
                          {showNewPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">{t("ยืนยันรหัสผ่านใหม่", "Confirm New Password")}</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <div className="pt-4">
                      <Button onClick={handleChangePassword} className="gap-2">
                        <Key className="h-4 w-4" />
                        {t("อัปเดตรหัสผ่าน", "Update Password")}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses">
            {user ? (
              <LicenseManagement userId={user.id} />
            ) : (
              <Card variant="glass">
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">{t("กรุณาเข้าสู่ระบบเพื่อดูโปรแกรมของคุณ", "Please log in to view your programs")}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Credit History Tab */}
          <TabsContent value="credits">
            <Card variant="glass">
              <CardHeader>
                <CardTitle>{t("ประวัติเครดิต", "Credit History")}</CardTitle>
                <CardDescription>{t("ดูรายการเครดิตของคุณ", "View your credit transactions")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {creditHistory.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.type === "purchase"
                          ? "bg-green-500/10"
                          : "bg-orange-500/10"
                          }`}>
                          {item.type === "purchase" ? (
                            <CreditCard className="h-5 w-5 text-green-500" />
                          ) : (
                            <Zap className="h-5 w-5 text-orange-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-muted-foreground">{item.date}</p>
                        </div>
                      </div>
                      <div className={`text-lg font-bold ${item.amount > 0 ? "text-green-500" : "text-orange-500"
                        }`}>
                        {item.amount > 0 ? "+" : ""}{item.amount.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Log Tab */}
          <TabsContent value="payments">
            <Card variant="glass">
              <CardHeader>
                <CardTitle>{t("ประวัติการซื้อโปรแกรม", "Program Purchase History")}</CardTitle>
                <CardDescription>
                  {t("ดูประวัติการซื้อโปรแกรมของคุณ", "View your program purchase history")}
                  {" · "}
                  <Link to="/credit-history" className="text-primary hover:underline">
                    {t("ดูประวัติการเติมเครดิต →", "View credit top-up history →")}
                  </Link>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {paymentLog.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">{t("ยังไม่มีประวัติการซื้อโปรแกรม", "No program purchase history yet")}</p>
                    <Link to="/credit-history">
                      <Button variant="outline" className="gap-2">
                        <CreditCard className="h-4 w-4" />
                        {t("ดูประวัติการเติมเครดิต", "View Credit History")}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4">{t("โปรแกรม", "Program")}</th>
                          <th className="text-right py-3 px-4">{t("จำนวนเงิน", "Amount")}</th>
                          <th className="text-left py-3 px-4">{t("วันที่", "Date")}</th>
                          <th className="text-left py-3 px-4">{t("วิธีชำระ", "Method")}</th>
                          <th className="text-center py-3 px-4">{t("สถานะ", "Status")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {paymentLog.map((payment) => (
                          <tr key={payment.id} className="hover:bg-muted/30">
                            <td className="py-3 px-4 font-medium">
                              {(payment as any).products?.name || t("โปรแกรม", "Program")}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-lg">฿{payment.amount.toLocaleString()}</td>
                            <td className="py-3 px-4 text-muted-foreground">
                              {payment.created_at ? new Date(payment.created_at).toLocaleString("th-TH") : "-"}
                            </td>
                            <td className="py-3 px-4 uppercase text-xs">{payment.payment_method || "-"}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${payment.status === 'completed'
                                ? "bg-green-500/10 text-green-500"
                                : payment.status === 'failed'
                                  ? "bg-red-500/10 text-red-500"
                                  : "bg-yellow-500/10 text-yellow-500"
                                }`}>
                                {payment.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                                {payment.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default Settings;